import { Box, Alert as MuiAlert, Snackbar, Typography } from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import React, { createContext, memo, useCallback, useContext, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual  } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { io } from 'socket.io-client';
import strings from '../../Globalization';
import {
	authSetLicense,
	authSetMenu,
	authSetModules,
	authSetRoles,
	authSetTags,
	authSetLoggedIn,
	authUnset,
	login as loginAction,
	setCyclesStatisticsCharts,
	setI18nContent,
	setI18nLanguage,
	setI18nLanguages
} from '../../actions';
import LoadingDialog from '../../components/common/LoadingDialog';
import consts from '../../consts';
import { useDidMount, useDidUpdate, useQueue, useSetState, useUniqueEffect } from '../../hooks';
import Connection from '../../services/Connection';
import util from '../../util';
import appConfig from '../../config/appConfig';
import RoutesGuard from './RoutesGuard';
import SessionTimeoutDialog from './SessionTimeoutDialog';


const csrfTokenMap = new Map([['name', 'x-csrf-token'], ['value', null]]);
// Monkey Patch native fetch
window.fetch = new Proxy(window.fetch, {
	apply: async (actualFetch, that, args) => {
		const [resource, config] = args;
		
		// Ensure credentials are included for requests to our API server
		if (config && !config.hasOwnProperty('credentials')) {
			const requestUrl = typeof resource === 'string' ? resource : resource?.url;
			if (requestUrl && requestUrl.startsWith(appConfig.ApiUrl)) {
				config.credentials = 'include';
			}
		}
		
		if (!["GET", "OPTIONS"].includes(config?.method)) {
			const csrfHeaderVal = csrfTokenMap.get('value');
			if (csrfHeaderVal) {
				const csrfHeaderName = csrfTokenMap.get('name');
				config.headers = {
					...config.headers,
					[csrfHeaderName]: csrfHeaderVal
				};
			}
		}

		// Forward function call to the original fetch
		const response = await Reflect.apply(actualFetch, that, args);
		//
		const res = response.clone();
		if (res?.url?.endsWith?.('.hot-update.json')) {
			const hotReloadEvent = new CustomEvent('hot-reload', {
				bubbles: true,
				detail: { response: res, resource, config },
			});
			window.dispatchEvent(hotReloadEvent);
		} else {
			const eventFetchResponse = new CustomEvent('fetch-response', {
				bubbles: true,
				detail: { response: res, resource, config },
			});
			window.dispatchEvent(eventFetchResponse);
		}

		return response;
	}
});
const USER_ASSOC_ROUTES = [
	'/userpasswordform'
];
const DASHBOARD_ASSOC_ROUTES = [
	'/modalitydashboard',
	'/equipmentinfo',
	'/dashboardtasks',
	'/dashboardcycles',
	'/dashboardincubator',
	'/incubatorcyclelist',
	'/cycleattachmentlist',
	'/incubatorcycledetailform'
];
const CYCLES_ASSOC_ROUTES = [
	'/cycleinfo',
	'/sterilizercycleinfo',
	'/endosystemcycleinfo',
	'/advantagecycleinfo',
	'/cycleattachmentlist',
	'/incubatorcycledetailform',
	'/trackcycle',
	'/enspirecycleinfo',
	'/transactionlist',
	'/endoscopelist',
	'/barcode'
];
const CATADMIN_ASSOC_ROUTES = [
	'/region',
	'/scoperegion',
	'/quicksearchscope',
	'/endoscopecodelist',
	'/itemsync',
	'/nonmedivator',
	'/endoSetupPhysician',
	'/endoprofile',
	'/hookupsync'
];
const ENDO_ASSOC_ROUTES = ['/endosystemcycleinfo', '/advantagecycleinfo', '/barcode','/interfaceConfig'];

const EQUIPMENT_ASSOC_ROUTES = ['/viewattachments', '/modellist'];
const ASSET_ASSOC_ROUTES = ['/assetscode'];



export const PrerequisitesContext = createContext([{ lang: 'en', strings: {}, ready: false, loading: true }, (prer) => { }]);
export const usePrerequisites = () => {
	const [prerequisites, prerequisitesControls] = useContext(PrerequisitesContext);

	return [prerequisites, prerequisitesControls];
};

const Alert = React.forwardRef(function Alert(props, ref) {
	return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

let socketClient = null;

const initializeSocket = (csrfToken) => {
	if (!socketClient) {
		socketClient = io(appConfig.ApiUrl, {
			autoConnect: true,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000,
			pingTimeout: 10000,
			pingInterval: 30000,
			withCredentials: true,
			extraHeaders: {
				[csrfTokenMap.get('name')]: csrfToken
			}
		});
	}
	return socketClient;
};

export const PrerequisitesProvider = ({ children }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const { auth, i18n, data } = useSelector((state) => ({auth: state.auth, i18n: state.i18n,data: state.login.data}), shallowEqual);
	const loggedin = auth?.loggedin;
	const [searchParams, setSearchParams] = useSearchParams();
	const toNavigateRef = useRef();
	const isPublicRoute = ['/', '/login', '/forgotpassword', '/setup'].includes(location.pathname);
	const productsRef = useRef([]);
	const redirectUrlRef = useRef('/');

	const [alert, alerts] = useQueue([], { auto: false });
	const [, errors] = useQueue([], { auto: false });
	const language = util.localData.PTXPreferredLang || data?.LicenseInfo?.defaultLanguage || i18n.language;
	strings.setLanguage(language);
	const [state, setState] = useSetState({
		language: language,
		session: util.localData.loginData,
		loaded: false,
		ready: false,
		initialPathname: location.pathname,
		passwordExpiredDialogOpen: false,
		sessionTimedout: false,
		baseRoute: '/'
	});

	const evalI18nContent = useCallback(
		(arr) => {
			const content =
				Array.isArray(arr) && arr?.length > 0
					? arr.reduce((acc, cur) => {
						if (!!cur.LanguageCode && cur.LanguageCode in acc) {
							acc[cur.LanguageCode][cur.LanguageKeyName] = cur.LanguageValue;
						} else if (cur.LanguageCode) {
							acc[cur.LanguageCode] = { [cur.LanguageKeyName]: cur.LanguageValue };
						}
						return acc;
					}, {})
					: { ...i18n?.content };
			return content;
		},
		[i18n?.content]
	);

	const onError = (err) => {
		const { info, icon = 'error', infoMessage, titleText, text, html, willClose, cursor, isHardStopPeriodOver, ...rest } = err;
		let contentText = typeof info === 'string' ? strings[info] : strings.CheckNetwork;
		if (typeof infoMessage === 'string' && infoMessage.length > 0) {
			contentText = strings[infoMessage] || infoMessage;
		}
		errors.enqueue(() => {
			try {
				Swal.fire({
					titleText: titleText || strings.ErrorTitle || 'Failed',
					text: text || contentText,
					html: html,
					icon: icon,
					allowOutsideClick: false,
					didClose: () => {
						errors.next();
						if (typeof willClose === 'function') {
							willClose();
						}
					},
					...rest
				}).then(({ isDenied }) => {
					if (isHardStopPeriodOver && isDenied) {
						logoutUser();
					}
				});
			} catch (error) {
				console.error(error);
			}
		});
		errors.start();
	};
	const onAlert = (alert) => {
		const { info, severity = 'info', infoMessage, title, text, onClose, ...rest } = { ...alert };
		let contentText = typeof info === 'string' ? strings[info] : strings.CheckNetwork;
		if (typeof infoMessage === 'string' && infoMessage.length > 0) {
			contentText = strings[infoMessage] || infoMessage;
		}
		alerts.enqueue({
			title: title || strings.Info || 'Failed',
			message: text || contentText,
			severity: severity || 'info',
			anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
			autoHideDuration: 3000,
			onClose: () => {
				alerts.next();
				if (typeof onClose === 'function') {
					onClose();
				}
			},
			...rest
		});
		alerts.start();
	};
	const uploadLicense = useCallback(
		(licFile, force = false) => {
			if (!licFile) {
				onError({
					icon: 'info',
					title: 'Info',
					confirmButtonText: strings.Ok || 'OK',
					text: `${strings.PleaseSelect || 'Please Select'} ${strings.LicensePath || 'License File'}`
				});
				return;
			}
			setState({
				loading: true,
				LicenseValid: !state.LicenseValid ? undefined : state.LicenseValid
			});
			const formData = new FormData();
			formData.append('action', 'uploadLicense');
			formData.append('force', force);
			formData.append('licFile', licFile);
			return fetch(Connection.Config, {
				method: 'POST',
				body: formData
			})
				.then((res) => res.json())
				.then((json) => {
					const refreshRequired = json.refreshRequired ?? false;
					if (json?.success) {
						if (json.info === 'ConfirmLicenseOverwrite') {
							Swal.fire({
								icon: 'warning',
								title: strings.LicenceWarning,
								confirmButtonText: strings.Ok || 'OK',
								confirmButtonColor: theme.palette.success.main,
								cancelButtonColor: theme.palette.error.main,
								showCancelButton: true,
								text: `${strings.LicenseOverwriteWarning ||
									'You are about to overwrite an existing license. Do you want to proceed?'}`
							}).then((result) => {
								if (result.isConfirmed) {
									uploadLicense(licFile, true);
								}
							});
						}
						else {
							Swal.fire({
								titleText: strings.Success,
								text: strings.LicenseUpdated,
								confirmButtonText: strings.Ok || 'OK',
								icon: 'success'
							});
							if (refreshRequired) {
								fetchData();
							}
							setLanguage(json.licenseInfo.DefaultLanguage);
						}

					} else {
						onError({
							titleText: strings.Error || 'Error',
							text: strings.LicenseUpdateFailed || 'Invalid license please check the CAT Version/Machine key on the license',
							confirmButtonText: strings.Ok || 'OK',
							icon: 'error',
							willClose: () => {
								setState({
									LicenseValid: !state.LicenseValid ? false : state.LicenseValid
								});
								window.location.reload();
							}
						});
					}
					return json;
				})
				.catch((err) => {
					onError(err);
				})
				.finally(() => {
					setState({ loading: false });
				});
		},
		[state.LicenseValid]
	);

	const applyMessageEffects = useCallback(
		(json) => {
			const message = json?.message;
			switch (message) {
				case 'IncorrectDatabase':
					break;
				case 'LicenseUpdateFailed':
					setState({
						LicenseValid: !state.LicenseValid ? undefined : state.LicenseValid
					});
					onError({
						titleText: strings.Error || 'Error',
						text: strings.LicenseUpdateFailed || 'Invalid License File',
						confirmButtonText: strings.Ok || 'OK',
						icon: 'error',
						preConfirm: () => {
							setState({
								LicenseValid: !state.LicenseValid ? false : state.LicenseValid
							});
						}
					});
					break;
				case 'LicenseUpdated':
					onError({
						titleText: strings.Success || 'Success',
						text: strings.LicenseUpdated || 'License Updated',
						confirmButtonText: strings.Ok || 'OK',
						icon: 'success'
					});
					window.location.reload();
					break;
				default:
					break;
			}
		},
		[strings, state.LicenseValid]
	);
	const onInvalidLicense = useCallback(() => {
		return onError({
			titleText: strings.LicenceWarning || 'License Warning',
			html: `${strings.LicenceMissing || 'Machine Key'} <br/> <br/> <strong><code>${state.MachineKey}</code></strong>`,
			input: 'file',
			showDenyButton: true,
			denyButtonColor: '#3c85d0',
			confirmButtonColor: '#04AA6D',
			confirmButtonText: strings.Ok || 'Ok',
			denyButtonText: strings.Copy || 'Copy',
			allowEscapeKey: false,
			allowOutsideClick: false,
			inputAttributes: {
				accept: '.lic,.zip',
				'aria-label': `${strings.LicenceMissing || 'Machine Key'}`
			},
			icon: 'warning',
			showLoaderOnConfirm: true,
			inputValidator: (value) => {
				if (!(value instanceof File)) {
					return 'Please Select License Path';
				}
			},
			preDeny: () => {
				if (navigator) {
					navigator.clipboard.writeText(state.MachineKey || '');
					alerts.enqueue({ message: strings.Copied || 'Copied', autoHideDuration: 1000 });
				}
				return false;
			},
			preConfirm: (licFile) => {
				uploadLicense(licFile);
			}
		});
	}, [state.MachineKey]);
	const evalI18nLanguages = useCallback(
		(langs) => {
			return Array.isArray(langs)
				? langs.map((lang) => ({
					id: lang.LookupId,
					label: lang.DisplayValue,
					...lang
				}))
				: i18n?.languages?.length > 0
					? i18n?.languages
					: [{ id: 'en', label: 'English' }];
		},
		[i18n?.languages]
	);

	const assertLicenseValidity = useCallback(
		(json, showNearingExpiryPrompts = false) => {
			const LicenseInfo = json?.LicenseInfo || json?.tags?.LicenseInfo;
			const LicenseValid = json?.tags?.LicenseValid || json?.LicenseValid;
			const MachineKey = json?.tags?.MachineKey || json?.MachineKey;

			if (
				('LicenseValid' in json && !json.LicenseValid) ||
				json.message === 'LicenceWarning' ||
				json.info === 'Invalid License file'
			) {
				setState({ LicenseValid: false, MachineKey, LicenseInfo });
			} else if (LicenseValid) {
				setState({ LicenseValid, MachineKey, LicenseInfo });
			}
			if (LicenseInfo?.isLicenseExpired) {
				const hardStopExpOn = dayjs(LicenseInfo?.hardStopExpOn);
				const hardStopDaysTillExpiry = hardStopExpOn?.isValid() ? hardStopExpOn.diff(dayjs(), 'day') : 0;
				const gracePeriodExpOn = dayjs(LicenseInfo?.gracePeriodExpOn);
				const graceDaysTillExpiry = gracePeriodExpOn?.isValid() ? gracePeriodExpOn.diff(dayjs(), 'day') : 0;

				if (LicenseInfo?.isGracePeriodOver) {
					if (!LicenseInfo?.isHardStopPeriodOver && LicenseInfo?.isInHardStopPeriod && showNearingExpiryPrompts) {
						onError({
							titleText: strings.LicenseInHardStop || 'License is currently in Hard Stop Period.',

							text: `${hardStopDaysTillExpiry > 0
								? (strings.LicenseExpiringIn || 'License will expire in {0} day(s).')?.replace?.(
									'{0}',
									hardStopDaysTillExpiry
								)
								: hardStopDaysTillExpiry === 0
									? strings?.LicenseExpireToday || 'License will expire today.'
									: ''
								} \n ${strings.AdminLicenseUpdate || 'Please contact the administrator to update the License.'
								} `,
							input: 'file',
							showCancelButton: true,
							cancelButtonColor: theme?.palette?.error?.main || '#FF0000',
							showDenyButton: false,
							denyButtonColor: '#3c85d0',
							confirmButtonColor: '#04AA6D',
							confirmButtonText: strings.Ok || 'Ok',
							inputAttributes: {
								accept: '.lic',
								'aria-label': `${strings.LicenceMissing || 'Machine Key'}`
							},
							showLoaderOnConfirm: true,
							inputValidator: (value) => {
								if (!(value instanceof File)) {
									return 'Please Select License Path';
								}
							},
							preConfirm: (licFile) => {
								errors.next();
								uploadLicense(licFile);
							},
							icon: 'warning'
						});
					}
					if (LicenseInfo?.isHardStopPeriodOver) {
						const pathname = window?.location?.pathname || location.pathname;
						if (pathname === '/appconfig') {
							navigate('/dashboard');
						}
						onError({
							titleText: strings.LicenseExpired || 'License Has Been Expired',
							text: strings.AdminLicenseUpdate || 'Please contact administrator to update the License.',
							confirmButtonText: strings.Ok || 'OK',
							denyButtonText: strings.Cancel || 'Cancel',
							showConfirmButton: true,
							showCancelButton: false,
							allowEscapeKey: false,
							allowOutsideClick: false,
							inputAttributes: {
								accept: '.lic',
								'aria-label': `${strings.LicenceMissing || 'Machine Key'}`
							},
							input: 'file',
							showDenyButton: true,
							showLoaderOnConfirm: true,
							inputValidator: (value) => {
								if (!(value instanceof File)) {
									return 'Please Select License Path';
								}
							},
							preConfirm: (licFile) => {
								errors.next();
								uploadLicense(licFile);
							},
							icon: 'error',
							confirmButtonColor: theme?.palette?.success?.main || '#00FF00',
							denyButtonColor: theme?.palette?.error?.main || '#00FF00',
							preDeny: () => {
								const pathname = window?.location?.pathname || location.pathname;
								if (pathname !== '/setup' && pathname !== '/' && pathname !== '/login') {
									logoutUser();
								}
							},
							isHardStopPeriodOver: LicenseInfo?.isHardStopPeriodOver
						});
					}
				} else if (LicenseInfo?.isInGracePeriod && showNearingExpiryPrompts) {
					onError({
						titleText: strings.LicenseInGracePeriod || 'License is currently in Grace Period.',
						text: `${graceDaysTillExpiry > 0
							? (strings.LicenseExpiringIn || 'License will expire in {0} day(s).')?.replace?.(
								'{0}',
								graceDaysTillExpiry
							)
							: ''
							} \n ${strings.AdminLicenseUpdate || 'Please contact the administrator to update the License.'} `,
						input: 'file',
						showDenyButton: false,
						showCancelButton: true,
						denyButtonColor: '#3c85d0',
						confirmButtonColor: '#04AA6D',
						cancelButtonColor: theme?.palette?.error?.main || '#FF0000',
						confirmButtonText: strings.Ok || 'Ok',
						allowEscapeKey: false,
						allowOutsideClick: false,
						inputAttributes: {
							accept: '.lic',
							'aria-label': `${strings.LicenceMissing || 'Machine Key'}`
						},
						showLoaderOnConfirm: true,
						inputValidator: (value) => {
							if (!(value instanceof File)) {
								return 'Please Select License Path';
							}
						},
						preConfirm: (licFile) => {
							errors.next();
							uploadLicense(licFile);
						},
						icon: 'warning'
					});
				}
			}
		},
		[location.pathname]
	);

	const checkUserLangValidity = async ({ reponseData = {} }) => {
		const { data = {} } = reponseData || {};
		const { LanguageCode, Active, ActiveLanguages } = data;

		const preferredLang = util?.localData?.loginData?.tags?.PreferredLang;
		if (!preferredLang) {
			dispatch(authUnset());
			dispatch(loginAction.logout());
			localStorage.setItem('IsLoggedOut', true);
			dispatch(authSetLoggedIn(false));
		}

		if (LanguageCode === preferredLang && !Active && ActiveLanguages > 0) {
			const formData = new FormData();
			formData.append('otherAction', 'getPreferredLang');
			formData.append('action', 'otherAction');
			const resp = await fetch(Connection.Language, {
				method: 'POST',
				body: formData
			});
			const { success = false, preferredLang = "en" } = await resp.json();
			if (success && preferredLang) {
				util.localData.loginData.tags.PreferredLang = preferredLang;
				fetchData();
			}
		}
	}


	const onFetchResponse = useCallback(
		async (event) => {
			const { resource, config, response } = event.detail;
			const headersIterator = response.headers.entries();
			const csrfHeaderNameRegex = new RegExp(`${csrfTokenMap.get('name')}`, 'i');
			let done = false;
			while (!done) {
				const entry = headersIterator.next();
				if (entry?.value) {
					const [header, value] = entry.value;
					if (csrfHeaderNameRegex.test(header)) {
						csrfTokenMap.set('value', value);
						// Initialize socket with CSRF token
						socketClient = initializeSocket(value);
						break;
					}
				}
				else {
					done = true;
				}
			}
			try {
				if (!config?.skipInterceptors) {
					const json = await response
						.clone()
						.json()
						.catch((err) => {
							// Do nothing
							return {};
						});
					if (json?.info === 'Session has expired!') {
						setState({ sessionTimedout: true });
					}

					if ((Connection.Language.includes(resource) || resource.includes(Connection.Language)) && resource !== Connection.Language) {
						await checkUserLangValidity({ reponseData: json });
					}
					if (typeof json === 'object' && (resource === Connection.Config || resource === Connection.Login)) {
						if (
							'tags' in json ||
							'LoginPageData' in json ||
							'langOptions' in json ||
							'langOptions' in json ||
							'appName' in json
						) {
							const { tags, langOptions, LoginPageData, appName } = json;
							let langs = langOptions || tags?.Language;
							const langData = LoginPageData || tags?.LoginPageData;

							if (Array.isArray(langs) && langs?.length > 0) {
								if (langs?.length > 0) {
									langs = evalI18nLanguages(langs);
									dispatch(setI18nLanguages(langs));
								}
							}
							if (Array.isArray(langData) && langData.length > 0) {
								const langContent = evalI18nContent(langData);
								strings.setContent(langContent);
								dispatch(setI18nContent(langContent));
							}
							const licenseProducts = tags?.AppName || appName || tags?.LicenseAppName;

							if (Array.isArray(licenseProducts)) {
								productsRef.current = licenseProducts;
							}
						}

						const payloadFormData = config.body instanceof FormData ? config.body : undefined;
						const payloadAction = payloadFormData?.get?.('action');
						const isLoginRequest =
							resource === Connection.Login &&
							config.method === 'POST' &&
							payloadFormData?.has?.('username') &&
							payloadFormData?.has?.('password') &&
							!payloadFormData?.has?.('action');
						const isGetConfigRequest = resource === Connection.Config && payloadAction === 'getConfig';
						if (isLoginRequest && json?.success) {
							onLogin(json, true);
						}
						const showNearingExpiryPrompts = isLoginRequest || isGetConfigRequest;

						assertLicenseValidity(json, showNearingExpiryPrompts);

						applyMessageEffects(json);
					}

					if (!response.ok) {
						let errorMsgs = new Map([
							[
								500,
								strings.CheckNetwork || 'Please check if STERIS ConnectAssure Technology Service is running'
							],
							[
								503,
								strings[json?.info] || json?.info ||
								'Server is currently unable to handle the request due to a temporary overload or maintenance'
							],
							[403, strings[json?.info] || json?.info || 'Server - Unavailable'],
							[429, strings[json?.info] || json?.info || 'Server is currently unable to handle the request due to a temporary overload or maintenance']
						]);
						if (!state.sessionTimedout && errorMsgs.has(response.status)) {
							onError({
								...response,
								infoMessage: errorMsgs.has(response.status)
									? errorMsgs.get(response.status)
									: response.statusText,
								preConfirm: () => {
									if (!state.ready) {
										window.location.reload();
									}

									return true;
								}
							});
						}
					}
				}
			} catch (error) { /* empty */ }
		},
		[location.pathname, state, isPublicRoute, loggedin, strings]
	);

	const setLanguage = useCallback(
		(language) => {
			strings.setLanguage(language);
			util.localData.PTXPreferredLang = language;
			util.setCookies('language', language, dayjs().add(1, 'month').toDate(), { path: '/' });
			dispatch(setI18nLanguage(language));
		},
		[language]
	);

	const getProductTypeRoutes = ({ menu, tags, roles }) => {
		const productRoutes = evalMenuPathnames(menu?.children);
		//To Do need to remove the static route
		const isCatRfidFound = tags.LicenseAppName?.includes?.('catrfid');
		const isAdminOrViewer = Array.isArray(roles)
			? util.getCommonFromArr(consts.adminViewerArray, roles).length > 0
			: false;
		let startRoute = toNavigateRef.current ? toNavigateRef.current : productRoutes[0];
		let productBaseRoute = productRoutes[0];

		if (isAdminOrViewer) {
			if (isCatRfidFound) {
				startRoute = productBaseRoute;
			} else {
				logoutUser();
				Swal.fire({
					titleText: 'Alert',
					text: strings.IncorrectLogin,
					confirmButtonText: strings.Ok || 'OK',
					icon: 'error',
					allowOutsideClick: false
				});
			}
		}
		if (tags?.IsPasswordExpired && productBaseRoute === '/appconfig') {
			productBaseRoute = productRoutes[0];
		}
		if (productBaseRoute?.startsWith?.('/setup') || productBaseRoute?.startsWith?.('/login')) {
			productBaseRoute = productRoutes[0];
		}

		return { productRoutes, productBaseRoute, startRoute };
	};
	const fetchStatisticsCharts = () => {
		const formData = new FormData();
		formData.append('id', 'Cycleen');
		formData.append('prefId', 0);
		formData.append('action', 'list');
		formData.append('prefName', 'StatisticsGraph');
		formData.append('isDefault', false);
		setState({
			fetching: true
		});
		fetch(Connection.GridPreferenceManager, {
			body: formData,
			method: 'POST',
			credentials: 'include'
		})
			.then((res) => res.json())
			.then(({ preferences }) => {
				const prefValues = Array.isArray(preferences)
					? preferences.reduce((acc, { prefValue }) => {
						try {
							const { defaultChartConfig } = { ...JSON.parse(prefValue) };
							if (Array.isArray(defaultChartConfig)) {
								acc = acc.concat(defaultChartConfig);
							}
						} catch (error) { /* empty */ }
						return acc;
					}, [])
					: [];
				if (prefValues?.length > 0) {
					dispatch(setCyclesStatisticsCharts(prefValues));
				}
			})
			.catch(() => {
			})
			.finally(() =>
				setState({
					fetching: false
				})
			);
	};

	const onLogin = useCallback(
		(loginInfo) => {
			loginInfo = 'data' in loginInfo ? loginInfo.data : loginInfo;
			if (loginInfo?.shouldChooseClient && loginInfo?.Clients?.length > 0) {
				const Clients = loginInfo?.Clients;
				setState({ shouldChooseClient: loginInfo?.shouldChooseClient, Clients, lastLoginClientId: loginInfo?.lastLoginClientId });
				return loginInfo;
			}
			const { productRoutes, productBaseRoute } = getProductTypeRoutes(loginInfo);
			dispatch(authSetLicense({ products: productsRef.current, routes: productRoutes, baseRoute: productBaseRoute, type: loginInfo.LicenseInfo.LicenseType }));
			const roles = Array.isArray(loginInfo.roles) ? loginInfo.roles : [];
			const tags = loginInfo.tags;
			const menu = loginInfo.menu;
			const modules = loginInfo.modules;
			const SelectedLanguageData = tags?.SelectedLanguageData;
			if (Array.isArray(SelectedLanguageData)) {
				delete tags.SelectedLanguageData;
				dispatch(setI18nContent(evalI18nContent(SelectedLanguageData)));
			}
			if (tags) {
				util.localData.LicenseAppName = tags?.LicenseAppName;
				if (!util.localData.PTXPreferredLang) {
					let lang = tags.PreferredLang;
					util.localData.PTXPreferredLang = lang;
					setLanguage(lang);
				}
			}

			const statePatch = { loading: false, ready: true, shouldChooseClient: false };
			if (tags?.IsPasswordExpired && !state.passwordExpiredDialogOpen) {
				statePatch.passwordExpiredDialogOpen = true;
			}
			localStorage.setItem('IsLoggedOut', false);
			dispatch(authSetLoggedIn(true));
			dispatch(authSetRoles(roles));
			dispatch(authSetTags(tags));
			dispatch(authSetMenu(menu));
			dispatch(authSetModules(modules));
			util.localData.loginData = loginInfo;
			util.localData.isSessionExpired = false;
			util.localData.username = tags?.Username || '';
			setState(statePatch);
			fetchStatisticsCharts();
		},
		[state.passwordExpiredDialogOpen, state.sessionTimedout, i18n.content, location]
	);

	const loginUser = (params = {}) => {
		const { username, password } = params;
		util.clearLocalStorage();
		util.localData.username = username;
		if (!username || !password) {
			let field = username === '' ? strings.Username : strings.Password;
			let errorMessage = strings.formatString(strings.requiredField, field);
			Swal.fire({ titleText: 'Alert', text: errorMessage, icon: 'success', confirmButtonText: strings.Ok, allowOutsideClick: false });
			return;
		} else {
			setState({ loading: true, sessionTimedout: false });
			const formData = new FormData();
			for (const [key, value] of Object.entries({ ...params, defaultLanguage: i18n.language })) {
				formData.append(key, value);
			}
			return fetch(Connection.Login, { method: 'POST', body: formData })
				.then((res) => res.json())
				.then((json) => {
					const { success, tags, message } = json;
					const passwordExpired = tags?.IsPasswordExpired || false;
					if (success) {
						return onLogin(json);
					} else {
						Swal.fire({ titleText: 'Alert', text: strings[message], icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
					}
					if (passwordExpired) {
						onPasswordExpired(json);
					}
				})
				.catch((err) => {
					const msg = err.message || err.msg;
					Swal.fire({ titleText: 'Error', text: strings[msg] ?? msg, icon: 'error', confirmButtonText: strings.Ok, allowOutsideClick: false });
				})
				.finally(() => setState({ loading: false }));
		}
	};

	const fetchData = useCallback(() => {
		setState({ loading: true });
		const formData = new FormData();
		formData.append("defaultLanguage", i18n.language);
		return fetch(Connection.Login, {
			method: 'GET', credentials: 'include'
		}).then((res) => res.json())
			.then((json) => {
				const { tags, message, LicenseInfo } = json;
				if (util?.localData?.PTXPreferredLang || tags?.PreferredLang || tags?.DefaultLanguage || LicenseInfo?.defaultLanguage) {
					let numberOfVisits = Number(util.getCookie("visits"));
					if (!numberOfVisits) {
						setLanguage(LicenseInfo?.defaultLanguage);
					} else {
						setLanguage(util?.localData?.PTXPreferredLang || tags?.PreferredLang || tags?.DefaultLanguage || LicenseInfo?.defaultLanguage);
					}
					numberOfVisits++;
					util.setCookies('visits', numberOfVisits, dayjs().add(1, 'month').toDate(), { path: '/' });
				}
				const success = json.success || json.Success;
				if (!success) {
					dispatch(authUnset());
					dispatch(loginAction.logout());
					localStorage.setItem('IsLoggedOut', true);
					dispatch(authSetLoggedIn(false));
				}

				const updatedState = JSON.parse(JSON.stringify(state));

				updatedState.session = json;
				updatedState.ready = true;
				if (success) {
					dispatch({
						type: loginAction.actions.SUCCESS,
						options: {},
						json: json,
						receivedAt: Date.now()
					});
				}
				util.localData.loginData = json;
				util.localData.isSessionExpired = false;
				util.localData.username = json?.tags?.Username || '';
				const inSetup = (json.redirectLogin === false && !['IncorrectLogin', 'Logged in'].includes(message)) || message === 'IncorrectDatabase'

				if (inSetup) {
					setState({ ...updatedState, inSetup: true, ready: true });
					return;
				}
				setState(updatedState);

				if (tags?.HasError) {
					Swal.fire({
						titleText: 'Error',
						confirmButtonText: strings.Ok || 'OK',
						text: strings.ErrorOccuredWhileCreatingDefaultUserUpdatingClientData,
						icon: 'error',
						showConfirmButton: false,
						allowOutsideClick: false
					});
					updatedState.hasError = true;
				}
				const isInvalidLicense =
					(typeof json === 'object' && 'LicenseValid' in json && !json?.LicenseValid) ||
					(typeof tags === 'object' && 'LicenseValid' in tags && !tags?.LicenseValid);
				if (isInvalidLicense) {
					Swal.fire({
						titleText: strings.LicenceWarning,
						text: strings.LicenceMissing,
						icon: 'input',
						input: 'text',
						inputAttributes: {
							readonly: true
						},
						cancelButtonText: strings.Copy,
						showCancelButton: true,
						closeOnConfirm: true,
						closeOnCancel: false,
						confirmButtonText: strings.Ok,
						inputValue: tags?.MachineKey,
						allowOutsideClick: false
					}).then((isConfirm) => {
						if (!isConfirm) {
							util.copyText(tags.MachineKey);
							Swal.fire({ text: strings.Copied , confirmButtonText: strings.Ok, allowOutsideClick: false});
						}
					});
					updatedState.invalidLicense = true;
				} else {
					let lang = util.localData.PTXPreferredLang || tags?.DefaultLanguage || language;
					setLanguage(lang);
					Object.assign(updatedState, {
						UpdateLogo: true,
						hospitalLogo: tags?.HospitalLogo,
						hospitalName: tags?.HospitalName,
						readOnly: Boolean(tags?.ReadOnly || false),
						clients: tags?.Clients || [],
						language: lang
					});
					if (success) {
						onLogin(json);
					}
				}
			})
			.catch((err) => {
				console.error('Error', err);
			})
			.finally(() => setState({ loading: false }));
	}, [state, i18n?.language, location]);

	useDidMount(() => {
		// Initialize once
		setLanguage(language);
		fetchData();
	});

	useUniqueEffect(() => {
		window.addEventListener('fetch-response', onFetchResponse);
	}, []);

	useDidUpdate(() => {
		if (state.LicenseValid === false) {
			onInvalidLicense();
		}
	}, [state.LicenseValid]);

	useUniqueEffect(() => {
		if (i18n.content) {
			strings.setContent(i18n.content);
		}
	}, [i18n.content]);

	const evalMenuPathnames = useCallback((menu) => {
		let pathnames = [];
		if (Array.isArray(menu)) {
			pathnames = menu.reduce((acc, curr) => {
				if (Array.isArray(curr?.children)) {
					acc = acc.concat(evalMenuPathnames(curr?.children));
				} else if (typeof curr?.url === 'string') {
					acc.push(`${curr.url.startsWith('/') ? '' : '/'}${curr.url.toLowerCase()}`);
				}
				return acc;
			}, []);
		}
		if (pathnames.includes('/dashboard')) {
			pathnames = pathnames.concat(DASHBOARD_ASSOC_ROUTES);
		}
		if (pathnames.includes('/userlist')) {
			pathnames = pathnames.concat(USER_ASSOC_ROUTES);
		}
		if (pathnames.includes('/cyclelist')) {
			pathnames = pathnames.concat(CYCLES_ASSOC_ROUTES);
		}
		if (pathnames.includes('/endosetupuser')) {
			pathnames = pathnames.concat(CATADMIN_ASSOC_ROUTES);
		}
		if (pathnames.includes('/endoCycleslist')) {
			pathnames = pathnames.concat(ENDO_ASSOC_ROUTES);
		}
		if (pathnames.includes('/asset')) {
			pathnames = pathnames.concat(ASSET_ASSOC_ROUTES);
		}
		if (pathnames.includes('/equipmentlist')) {
			pathnames = pathnames.concat(EQUIPMENT_ASSOC_ROUTES);
		}

		if (pathnames.includes('/scoperegion') && !pathnames.includes('/region')) {
			pathnames = pathnames.concat(['/region']);
		}
		if (pathnames.includes('/languagemapperlist')) {
			pathnames = pathnames.concat(['/languagelist']);
		}
		if (pathnames.includes('/authorizedclients') && !pathnames.includes('/command')) {
			pathnames = pathnames.concat(['/command']);
		}
		pathnames = pathnames.concat(['/videolist', '/userinfo']);
		return pathnames;
	}, []);

	const navigateToBaseRoute = useCallback(
		(msg = true, returnTo = null) => {
			if (auth?.license?.baseRoute || returnTo) {
				navigate('/forbiddenpage');
			}
		},
		[auth?.license?.baseRoute, navigate, location]
	);

	const navigateToLastAccessibleRoute = useCallback(
		(msg = true) => navigateToBaseRoute(msg, redirectUrlRef.current),
		[navigateToBaseRoute]
	);

	const logoutUser = (callback) => {
		setState({
			loading: true,
			initialPathname: `${location?.pathname}${searchParams?.toString() ? '?' : ''}${searchParams?.toString()}`,
			sessionTimedout: false
		});
		const searchParamsObj = {};
		for (const [key, value] of searchParams.entries()) {
			searchParamsObj[key] = value;
		}
		searchParamsObj.isLogout = 1;
		setSearchParams(searchParamsObj);
		const formData = new FormData();
		formData.append('signOut', true);
		
		fetch(Connection.Login, {
			body: formData,
			method: 'POST',
			credentials: 'include'
		})
			.then((response) => {
				if (response.status === 200) {
					const newPathname = `/login?isLogout=1`;
					dispatch(authSetLoggedIn(false));
					dispatch(loginAction.logout());
					dispatch(authUnset());
					util.deleteCookie('user_sid');
					fetchData();
					localStorage.setItem('IsLoggedOut', true);
					if (typeof callback === 'function') {
						callback(newPathname);
					}
				}
			})
			.catch(function (err) {
				console.error(err)
			})
			.finally(() => setState({ loading: false, sessionTimedout: false }));
	};

	const onChangePasswordSubmit = useCallback(
		(values) => {
			if (values.OldPassword === values.NewPassword) {
				return onError({
					titleText: strings.Info,
					text: strings.NewPasswordIsSameAsOld || 'New Password should not be same as old password',
					icon: 'error'
				});
			} else if (!values.NewPassword) {
				return onError({
					titleText: strings.Info,
					text: strings.EnterNewPassword || 'Enter New Password',
					icon: 'error'
				});
			}
			setState({ loading: true });
			const formData = new FormData();
			formData.append('action', 'changepassword');
			formData.append('OldPassword', values.OldPassword);
			formData.append('NewPassword', values.NewPassword);
			formData.append('ConfirmPassword', values.ConfirmPassword);
			fetch(Connection.Login, { method: 'POST', body: formData })
				.then((res) => res.json())
				.then(({ info, success }) => {
					if (success && info === consts.ResponseText.PasswordChanged) {
						setState({ passwordExpiredDialogOpen: false });
						logoutUser();
						onAlert({
							severity: 'success',
							text: strings[info] || 'Password has been successfully changed.',
							actionText: strings.Ok || 'Ok'
						});
					} else {
						onError({ title: strings.Info, text: strings[info], icon: 'error' });
					}
				})
				.catch((err) => {
					onError({
						title: strings.ErrorTitle,
						text: strings.ErrorUpdate || 'Some error occured during update',
						icon: 'error'
					});
				})
				.finally(() => setState({ loading: false, passwordExpiredDialogOpen: false }));
		},
		[strings]
	);

	const onPasswordExpired = () => setState({ passwordExpiredDialogOpen: true });
	const getLicenseType = useCallback(() => {
		const products = productsRef.current;
		return {
			catsync: products?.includes?.('catsync') ?? false,
			catviewer: products?.includes?.('catviewer') ?? false,
			catexport: products?.includes?.('catexport') ?? false,
			catedge: products?.includes?.('catedge') ?? false,
			multi: products?.length > 1,
			products
		};
	}, []);

	const handleOnCloseSesssionTimeout = () => logoutUser();

	useUniqueEffect(() => {
		const hasAccess = Array.isArray(auth?.license?.routes) &&
			auth?.license.routes.some((route) => route.toLowerCase() === location.pathname.toLowerCase()) //routes paths are case insensitive
		if (auth?.loggedin &&
			location.pathname !== '/' &&
			location.pathname.toLowerCase() !== '/login' &&
			!hasAccess) {
			navigateToLastAccessibleRoute(`<div>
								<p>${location.pathname}${searchParams.size > 0 ? '?' : ''}${searchParams.toString()}</p>
								${strings.AccessError || 'You do not have access'}
							</div>`);
		} else if (auth?.loggedin) {
			redirectUrlRef.current = `${location.pathname === '/' ? auth?.license?.baseRoute : location.pathname}${searchParams.size > 0 ? '?' : ''
				}${searchParams.toString()}`;
		} else {
			redirectUrlRef.current = null;
		}
	}, [location.pathname]);

	return (
		<PrerequisitesContext.Provider
			value={[
				{ ...state, strings, language, socketClient, csrfTokenMap  },
				{
					setLanguage,
					uploadLicense,
					onLogin,
					onPasswordExpired,
					logoutUser,
					loginUser,
					getLicenseType,
					onChangePasswordSubmit,
					navigateToBaseRoute,
					navigateToLastAccessibleRoute,
				},
			]}
		>
			<GlobalStyles
				styles={{
					body: {
						margin: 0
					},
					'& .MuiDataGrid-menu': {
						zIndex: `${theme.zIndex.dataGridMenu}`
					},
					'& .MuiDataGrid-panel': {
						zIndex: `${theme.zIndex.dataGridPanel}`
					},
					'& .MuiAutocomplete-popper': {
						zIndex: `${theme.zIndex.autoCompletePopper} !important`
					},
					'& .GridToolbarExportBtn': {
						backgroundColor: `${theme.palette.primary.main} !important`,
						'&:hover': {
							backgroundColor: `${theme.palette.primary.dark} !important`
						}
					},
					'& .MuiCardContent-root': {
						'&:last-child': {
							paddingBottom: 'initial'
						}
					},
					'& .text-error': {
						color: theme.palette.error.main
					},
					'& .HeaderFilterForm': {
						[theme.breakpoints.up('md')]: {
							width: '100%'
						},
						[theme.breakpoints.up('lg')]: {
							width: '50%'
						}
					}
				}}
			/>
			<RoutesGuard />
			{state.ready && <Box sx={{ flexGrow: 1 }}>{children}</Box>}
			<Snackbar
				autoHideDuration={5000}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				sx={{ zIndex: 99999 }}
				onClose={alerts.next}
				{...alert}
				open={!!alert && !state.loading}
			>
				<Alert
					severity={alert?.severity || 'info'}
					sx={{
						width: '100%',
						flexDirection: 'row-reverse',
						'& .MuiAlert-icon': { marginRight: 0, marginLeft: 2, fontSize: '1.25rem' }
					}}
				>
					<Box display={'flex'} flexDirection={'column'} rowGap={1}>
						{alert?.title && (
							<Typography color={'inherit'} variant="body2" gutterBottom>
								{alert?.title}
							</Typography>
						)}
						{alert?.message && (
							<Typography color={'inherit'} variant="body2">
								{alert?.message}
							</Typography>
						)}
					</Box>
				</Alert>
			</Snackbar>
			<SessionTimeoutDialog
				open={state.sessionTimedout && !isPublicRoute && loggedin && state.ready}
				onClose={handleOnCloseSesssionTimeout}
			/>
			<LoadingDialog open={state.loading} />
		</PrerequisitesContext.Provider>
	);
};
export const withPrerequisites = (Component) => {
	const wrapComponent =
		(Component) =>
			({ children, ...props }) => {
				const prerequisites = usePrerequisites();
				return (
					<Component prerequisites={prerequisites} {...props}>
						{children}
					</Component>
				);
			};
	return wrapComponent(Component);
};

export default memo(PrerequisitesProvider);
